import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  TextField, 
  Button, 
  Avatar, 
  Grid, 
  Divider, 
  Paper, 
  MenuItem, 
  Select, 
  FormControl, 
  InputLabel, 
  Chip
} from '@mui/material';
import { Send as SendIcon, Reply as ReplyIcon, Check as CheckIcon } from '@mui/icons-material';
import { Feedback, FeedbackFormData, FeedbackType } from '../types/feedback';
import { feedbackService } from '../services/feedback-service';
import { formatDistance } from 'date-fns';
import { UserRole } from '../types/user';

interface FeedbackSectionProps {
  projectId: string;
  projectStatus?: string;
  hasProgressUpdates?: boolean;
  showUpdateButton?: boolean;
  onUpdateProgress?: () => void;
}

export default function FeedbackSection({ projectId, projectStatus, hasProgressUpdates, showUpdateButton, onUpdateProgress }: FeedbackSectionProps) {
  const [feedbackList, setFeedbackList] = useState<Feedback[]>([]);
  const [newFeedback, setNewFeedback] = useState<FeedbackFormData>({
    content: '',
    type: 'question',
    projectId: projectId
  });
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const currentUser = feedbackService.getCurrentUser();

  // Determine if user can provide feedback based on role and project status
  const canProvideFeedback = () => {
    if (!currentUser) return false;
    
      
    
    // Supervisor (Subcounty user) can provide feedback on ongoing projects
    if (currentUser.role === UserRole.SUBCOUNTY_USER && projectStatus === 'ongoing') {
      return true;
    }
    
    // WIT user (County user) can provide feedback on projects with Q1 updates (completed first progress update)
    if (currentUser.role === UserRole.COUNTY_USER && hasProgressUpdates) {
      return true;
    }
    
    return false;
  };
  
  // Load feedback for this project
  useEffect(() => {
    const loadFeedback = async () => {
      try {
        setLoading(true);
        const data = await feedbackService.getProjectFeedback(projectId);
        setFeedbackList(data);
        setError('');
      } catch (err) {
        setError('Failed to load feedback. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    loadFeedback();
  }, [projectId]);
  
  // Group feedback by thread (parent-child relationships)
  const organizedFeedback = () => {
    const threads: { parent: Feedback, replies: Feedback[] }[] = [];
    const parentFeedback = feedbackList.filter(f => !f.parentId);
    
    parentFeedback.forEach(parent => {
      const replies = feedbackList.filter(f => f.parentId === parent.id);
      threads.push({ parent, replies });
    });
    
    // Sort threads by most recent activity (parent or last reply)
    return threads.sort((a, b) => {
      const aLatest = a.replies.length > 0 ? 
        new Date(a.replies[a.replies.length - 1].createdAt) : 
        new Date(a.parent.createdAt);
      
      const bLatest = b.replies.length > 0 ? 
        new Date(b.replies[b.replies.length - 1].createdAt) : 
        new Date(b.parent.createdAt);
        
      return bLatest.getTime() - aLatest.getTime();
    });
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newFeedback.content.trim()) {
      return;
    }
    
    try {
      setLoading(true);
      const feedbackToSubmit = {
        ...newFeedback,
        parentId: replyTo || undefined,
        type: replyTo ? 'response' : newFeedback.type
      };
      
      const addedFeedback = await feedbackService.addFeedback(feedbackToSubmit);
      setFeedbackList([...feedbackList, addedFeedback]);
      
      // Reset form
      setNewFeedback({
        content: '',
        type: 'question',
        projectId: projectId
      });
      setReplyTo(null);
    } catch (err) {
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleReplyClick = (feedbackId: string) => {
    setReplyTo(feedbackId);
    
    // Find the feedback being replied to
    const feedback = feedbackList.find(f => f.id === feedbackId);
    if (feedback) {
      // Auto-focus the input field
      document.getElementById('feedback-input')?.focus();
    }
  };
  
  const handleCancelReply = () => {
    setReplyTo(null);
  };
  
  const handleMarkAsResolved = async (feedbackId: string) => {
    if (!currentUser) {
      setError('You must be logged in to resolve feedback.');
      return;
    }
    
    try {
      const updatedFeedback = await feedbackService.updateFeedback(feedbackId, {
        resolved: true,
        resolvedAt: new Date().toISOString(),
        resolvedBy: currentUser.id
      });
      
      setFeedbackList(feedbackList.map(f => 
        f.id === feedbackId ? updatedFeedback : f
      ));
    } catch (err) {
      setError('Failed to mark feedback as resolved.');
    }
  };
  
  // Determine if current user can mark items as resolved (WIT or facility roles)
  const canResolve = currentUser ? [UserRole.COUNTY_USER, UserRole.FACILITY_USER, UserRole.CQI_MANAGER].includes(currentUser.role) : false;

  // Helper to get user color based on role
  const getUserColor = (role: UserRole) => {
    switch (role) {
      case UserRole.SUBCOUNTY_USER: return '#8bc34a'; // Green
      case UserRole.FACILITY_USER: return '#2196f3'; // Blue
      case UserRole.COUNTY_USER: return '#ff9800'; // Orange for WIT/County users
      case UserRole.NATIONAL_USER: return '#9c27b0'; // Purple
      case UserRole.CQI_MANAGER: return '#f44336'; // Red
      default: return '#9e9e9e'; // Grey
    }
  };
  
  // Helper to format date in a readable way
  const formatDate = (dateString: string) => {
    return formatDistance(new Date(dateString), new Date(), { addSuffix: true });
  };
  
  return (
    <Card sx={{ mt: 3, boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
            💬 Project Feedback &amp; Discussion
          </Typography>
          {showUpdateButton && onUpdateProgress && (
            <Button
              variant="contained"
              color="primary"
              onClick={onUpdateProgress}
              startIcon={<SendIcon />}
            >
              Update Progress
            </Button>
          )}
        </Box>
        <Divider sx={{ mb: 3 }} />
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            {error && (
              <Paper sx={{ p: 2, mb: 2, bgcolor: '#ffebee' }}>
                <Typography color="error">{error}</Typography>
              </Paper>
            )}
            
            <Box sx={{ mb: 3, maxHeight: '400px', overflowY: 'auto', p: 1 }}>
          {organizedFeedback().length > 0 ? (
            organizedFeedback().map(({ parent, replies }) => (
              <Box key={parent.id} sx={{ mb: 3 }}>
                {/* Parent feedback */}
                <Paper 
                  elevation={1} 
                  sx={{ 
                    p: 2, 
                    mb: 1, 
                    bgcolor: parent.resolved ? '#f5f5f5' : '#fff',
                    border: `1px solid ${getUserColor(parent.createdBy.role)}` 
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Avatar 
                      sx={{ 
                        mr: 1, 
                        bgcolor: getUserColor(parent.createdBy.role),
                        width: 32,
                        height: 32,
                        fontSize: '0.875rem'
                      }}
                    >
                      {parent.createdBy.name.charAt(0)}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          {parent.createdBy.name} 
                          <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                            ({parent.createdBy.role}) - {parent.createdBy.organization}
                          </Typography>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(parent.createdAt)}
                        </Typography>
                      </Box>
                      <Chip 
                        label={parent.type.charAt(0).toUpperCase() + parent.type.slice(1)} 
                        size="small" 
                        sx={{ mb: 1 }} 
                        color={
                          parent.type === 'question' ? 'info' : 
                          parent.type === 'concern' ? 'error' : 
                          parent.type === 'suggestion' ? 'success' : 'default'
                        }
                      />
                      <Typography variant="body2">{parent.content}</Typography>
                      
                      {/* Action buttons */}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                        <Button 
                          size="small" 
                          startIcon={<ReplyIcon />} 
                          onClick={() => handleReplyClick(parent.id)}
                        >
                          Reply
                        </Button>
                        
                        {canResolve && !parent.resolved && (
                          <Button 
                            size="small" 
                            startIcon={<CheckIcon />} 
                            color="success" 
                            sx={{ ml: 1 }}
                            onClick={() => handleMarkAsResolved(parent.id)}
                          >
                            Mark Resolved
                          </Button>
                        )}
                        
                        {parent.resolved && (
                          <Chip 
                            label="Resolved" 
                            size="small" 
                            color="success" 
                            sx={{ ml: 1 }} 
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Paper>
                
                {/* Replies */}
                {replies.length > 0 && (
                  <Box sx={{ pl: 4 }}>
                    {replies.map(reply => (
                      <Paper 
                        key={reply.id} 
                        elevation={1} 
                        sx={{ 
                          p: 2, 
                          mb: 1, 
                          bgcolor: '#f8f9fa',
                          border: `1px solid ${getUserColor(reply.createdBy.role)}`,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                          <Avatar 
                            sx={{ 
                              mr: 1, 
                              bgcolor: getUserColor(reply.createdBy.role),
                              width: 32,
                              height: 32,
                              fontSize: '0.875rem'
                            }}
                          >
                            {reply.createdBy.name.charAt(0)}
                          </Avatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                {reply.createdBy.name}
                                <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                  ({reply.createdBy.role}) - {reply.createdBy.organization}
                                </Typography>
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(reply.createdAt)}
                              </Typography>
                            </Box>
                            <Typography variant="body2">{reply.content}</Typography>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                )}
              </Box>
            ))
          ) : (
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#f5f5f5' }}>
              <Typography variant="body2" color="text.secondary">
                No feedback has been shared yet. Be the first to start a discussion!
              </Typography>
            </Paper>
          )}
        </Box>
        
        {/* New feedback form - only visible if user has permission */}
        {canProvideFeedback() ? (
          <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
            <form onSubmit={handleSubmit}>
            {replyTo && (
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <ReplyIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">
                  Replying to: {feedbackList.find(f => f.id === replyTo)?.content.substring(0, 50)}
                  {(feedbackList.find(f => f.id === replyTo)?.content.length || 0) > 50 ? '...' : ''}
                </Typography>
                <Button size="small" sx={{ ml: 1 }} onClick={handleCancelReply}>
                  Cancel
                </Button>
              </Box>
            )}
            
            <Grid container spacing={2}>
              {!replyTo && (
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="feedback-type-label">Feedback Type</InputLabel>
                    <Select
                      labelId="feedback-type-label"
                      value={newFeedback.type}
                      label="Feedback Type"
                      onChange={(e) => setNewFeedback({
                        ...newFeedback,
                        type: e.target.value as FeedbackType
                      })}
                    >
                      <MenuItem value="question">Question</MenuItem>
                      <MenuItem value="suggestion">Suggestion</MenuItem>
                      <MenuItem value="concern">Concern</MenuItem>
                      <MenuItem value="update">Status Update</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
              
              <Grid item xs={12} sm={replyTo ? 10 : 6}>
                <TextField
                  id="feedback-input"
                  placeholder={replyTo ? "Type your reply..." : "Share your feedback, question, or suggestion..."}
                  multiline
                  rows={2}
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={newFeedback.content}
                  onChange={(e) => setNewFeedback({
                    ...newFeedback,
                    content: e.target.value
                  })}
                />
              </Grid>
              
              <Grid item xs={12} sm={2} sx={{ display: 'flex', alignItems: 'center' }}>
                <Button 
                  type="submit" 
                  variant="contained" 
                  endIcon={<SendIcon />}
                  disabled={loading || !newFeedback.content.trim()}
                  fullWidth
                >
                  {replyTo ? 'Reply' : 'Send'}
                </Button>
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
              {currentUser && (
                <>
                  <Avatar 
                    sx={{ 
                      width: 24, 
                      height: 24, 
                      fontSize: '0.75rem',
                      bgcolor: getUserColor(currentUser.role),
                      mr: 1
                    }}
                  >
                    {currentUser.name.charAt(0)}
                  </Avatar>
                  <Typography variant="caption" color="text.secondary">
                    Posting as {currentUser.name} ({currentUser.role})
                  </Typography>
                </>
              )}
            </Box>
          </form>
        </Paper>
        ) : (
          <Paper elevation={0} sx={{ p: 3, bgcolor: '#fff3cd', borderRadius: 2, border: '1px solid #ffc107' }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              {currentUser?.role === UserRole.SUBCOUNTY_USER && projectStatus !== 'ongoing' && (
                '📋 Supervisors can provide feedback on ongoing projects only.'
              )}
              {currentUser?.role === UserRole.COUNTY_USER && !hasProgressUpdates && (
                '📊 WIT users can provide feedback after the first Q1 project update has been submitted.'
              )}
              {currentUser?.role === UserRole.SUBCOUNTY_USER && projectStatus === 'ongoing' && (
                '✅ You can provide feedback on this ongoing project.'
              )}
              {currentUser?.role === UserRole.COUNTY_USER && hasProgressUpdates && (
                '✅ You can provide feedback on this project with Q1 updates.'
              )}
              {!currentUser?.role && (
                '🔒 You need to be logged in to provide feedback.'
              )}
            </Typography>
          </Paper>
        )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
